/**
 * RevenuesPerYearSection - Zeigt ein Diagramm der jährlichen Erlöse nach Tarifquelle
 * 
 * Die Chart-Daten werden im Frontend berechnet (Client-Side Derived State).
 * Über den Aktualisieren-Button können die Backend-berechneten Erlöse geladen werden.
 */
import React from 'react';
import { Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import Section from '../../../layout/Section';
import Button from '../../../common/inputs/Button';
import TariffRevenueChart from '../../../charts/TariffRevenueChart';
import { EnergyRevenuesResponse } from '../../../../types';

interface RevenuesPerYearSectionProps {
  /** Energieerlöse (im Frontend berechnet) */
  energyRevenues: EnergyRevenuesResponse | null;
  /** Callback zum Laden der Backend-Erlöse (Aktualisieren-Button) */
  onRefresh?: () => void;
  /** Ob gerade ein Refresh läuft */
  isRefreshing?: boolean;
}

/**
 * Zeigt ein gestapeltes Säulendiagramm mit jährlichen Erlösen nach Tarifquelle.
 * Enthält einen Aktualisieren-Button zum manuellen Laden der Backend-Erlöse.
 */
const RevenuesPerYearSection: React.FC<RevenuesPerYearSectionProps> = ({
  energyRevenues,
  onRefresh,
  isRefreshing = false,
}) => {
  // Aktualisieren-Button als Action für die Section
  const refreshAction = onRefresh ? (
    <Button
      variant="secondary"
      startIcon={<RefreshIcon />}
      onClick={onRefresh}
      disabled={isRefreshing}
    >
      {isRefreshing ? 'Wird geladen...' : 'Aktualisieren'}
    </Button>
  ) : undefined;

  // Keine Erlösdaten vorhanden
  if (!energyRevenues || !energyRevenues.eintraege || energyRevenues.eintraege.length === 0) {
    return (
      <Section title="Erlöse pro Jahr" actions={refreshAction}>
        <Alert severity="info">
          Keine Erlösdaten verfügbar. Bitte stelle sicher, dass eine aktive Ertragsprognose und Tarife konfiguriert sind.
        </Alert>
      </Section>
    );
  }

  return (
    <Section title="Erlöse pro Jahr" actions={refreshAction}>
      <TariffRevenueChart
        energyRevenues={energyRevenues}
        height={350}
      />
    </Section>
  );
};

export default React.memo(RevenuesPerYearSection);
